// 指摘内容は1ファイルの最大行数（500行）の超過であるため、処理内容自体の問題はないと判断。
/* eslint-disable max-lines */

// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import api from '../../../common/api';
import config from '../../../share/config';
import session from '../../../share/session';
import handler from '../../../share/util/apiResultValidator';
import error from '../../../common/nodejs/errors';
import constants from '../../../common/nodejs/constants';
import alert from '../../../share/alert';
import macKeyInspection from '../../vue/MACKeyInspection';
import macKeyUpdate from '../../vue/MACKeyUpdate';
import macAuthKeyRegistration from '../../vue/MACAuthKeyRegistration';
import macKey from '../../../share/util/macKey';
import eventLogUtil from '../../../share/util/eventLogUtil';
import { MAC_SYSTEM_NAME } from './constants';

const MAC_KEY_SYSTEM_LIST = [constants.MC, constants.ESGW, constants.EBB, constants.SAS, constants.EPS, constants.ES, constants.BC, constants.TM, constants.EGI, constants.CGW, constants.CECU, constants.TLM_25ePF, MAC_SYSTEM_NAME.HPC_SHEV, MAC_SYSTEM_NAME.HPC_SHEV_P6];

export default {

  data() {
    return {
      watcher: null,
      macStatusInfo: {},
      macUpdateInfo: {},
      tempGetMacStatusResponse: {},
      isReturnedFromDisconnection: false,
      endOfProcessingFlag: false,
      executeRspMsgCheckTimeout: Number(process.env.VUE_APP_MAC_CC_RSP_MAX_INTERVAL),
      currentDialogId: null, // 現在表示中のダイアログのID,
      authKeyStatusInfo: {}, // 機器間認証用鍵登録のステータス
      // dialog表示のための変数
      modalShowInfo: {},
      modal_return_to_home_screen: false
    };
  },

  // router params対応
  props: ["response"],

  components: {
    'mac-key-inspection': macKeyInspection,
    'mac-key-update': macKeyUpdate,
    'mac-auth-key-registration': macAuthKeyRegistration
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
    }),
  },

  watch: {
    endOfProcessingFlag: function (value) {
      if (this.isReturnedFromDisconnection && value) {
        this.modal_return_to_home_screen = true;
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),
    ...mapActions('netAppConnector', ['getPairingStatus']),
    ...mapActions('observation', ['usageCountUp']),

    /**
     * MAC鍵検証処理を開始する
     */
    onInspectionButtonClick: async function () {
      try {
        // イベントログのページビューカウント
        eventLogUtil.eventLogPageViewCount(constants.EVENT_LOG_PAGE_VIEW_PAGE_NAME_MAC_KEY);

        // MAC鍵のカウントを1つ加算する
        this.usageCountUp({ type: config.USAGE_COUNT_MACKEY });
        this.currentDialogId = "";
        this.dialogAction('modal-key-inspection-platform-gen-decision');
        const token = session.getToken();

        await this.createEventLogRecord(constants.FUNCTION_NAME_INSPECTION_MAC_KEY);
        const startMacResponse = await api.postCall(config.MAC, '/inspection/' + this.netAppId, {
          id_token: token.id_token,
        });

        const isSuccess = handler.validate(handler.validateTypes.all, startMacResponse, this, null, () => { },
          (result) => {
            const cause = result.causeType;
            const behaviorType = result.behaviorType;
            // Logout if session is invalid or network error
            if (cause === handler.causeTypes.networkError ||
              cause === handler.causeTypes.invalidSessionToken) {
              this.clearWatcher();
              alert.showWarning(this, null, result.message, () => {
                if (behaviorType === handler.behaviorTypes.logout) {
                  this.logout(this);
                } else if (behaviorType === handler.behaviorTypes.resetSession) {
                  this.resetSession(this);
                }
              });
              return;
            } else {
              // Other errors
              this.showUnexpectedErrorModal(result.message);
            }
            return;
          }, null, false);
        if (!isSuccess) {
          this.modalShowInfo = { id: this.currentDialogId, isShow: false };
          return;
        }

        // Start Mac status monitoring
        this.setupWatcher(true);
      } catch (error) {
        this.modalShowInfo = { id: this.currentDialogId, isShow: false };
      }
    },

    /**
     * MAC鍵機能の実行状況を監視する
     */
    getMacStatus: async function () {
      this.macStatusInfo = {};
      this.executeRspMsgCheckTimeout -= (process.env.VUE_APP_RETRY_INTERVAL / 1000);
      try {
        let getMacStatusResponse = await api.getCall(config.MAC, '/getMacStatus/' + this.netAppId);
        const isSuccess = handler.validate(handler.validateTypes.all, getMacStatusResponse, this, null, () => { },
          (result) => {
            const cause = result.causeType;
            const behaviorType = result.behaviorType;
            // Logout if session is invalid or network error
            if (cause === handler.causeTypes.networkError ||
              cause === handler.causeTypes.invalidSessionToken) {
              this.clearWatcher();
              alert.showWarning(this, null, result.message, () => {
                if (behaviorType === handler.behaviorTypes.logout) {
                  this.logout(this);
                } else if (behaviorType === handler.behaviorTypes.resetSession) {
                  this.resetSession(this);
                }
              });
              return;
            } else if (cause === handler.causeTypes.clientConnectorNotAliveError) {
              // CC is not running
              this.showCCNotAliveModal();
            } else {
              // Other errors
              this.showUnexpectedErrorModal(result.message);
            }
            return;
          }, null, false);
        if (!isSuccess) {
          return;
        }

        let pairingStatus = null;
        // ステータスの問い合わせ
        const param = {
          this: this,
          netAppId: null
        };
        const pairingResponse = await this.getPairingStatus(param);
        pairingStatus = pairingResponse.response.data;
        // MACは実行中に一定時間 CC からの応答がなければ処理終了
        if (!pairingStatus.repro_exec_rsp_received && this.executeRspMsgCheckTimeout <= 0) {
          this.showUnexpectedErrorModal(this.$t('error.IOT_DOES_NOT_ARRICE_FROM_CC'));
          return;
        }

        getMacStatusResponse = getMacStatusResponse.data;
        if (getMacStatusResponse.process_name === constants.MAC_KEY_PROCESS_INSPECTION) {
          this.macKeyInspectionProcess(getMacStatusResponse);
        } else if (getMacStatusResponse.process_name === constants.MAC_KEY_PROCESS_UPDATE) {
          this.macKeyUpdateProcess(getMacStatusResponse);
        } else if (getMacStatusResponse.process_name === constants.MAC_KEY_PROCESS_REGISTRATION) {
          this.authKeyRegistrationProcess(getMacStatusResponse);
        }
      } catch (error) {
        // 'Unexpected error has occurred'
      }
    },

    /**
     * MAC鍵検証のダイアログ切り替え処理を行う
     * @param {Object} getMacStatusResponse 現在のMAC鍵ステータス
     */
    macKeyInspectionProcess: function (getMacStatusResponse) {
      const progress = macKey.getMacKeyProgress(getMacStatusResponse, constants.MAC_KEY_PROCESS_INSPECTION);
      this.dialogAction(progress.dialogId, progress.stopWatching);

      // ダイアログ表示に使用するプロパティを変換する
      const convertedSystems = [];
      if (getMacStatusResponse.systems && getMacStatusResponse.systems.length > 0) {
        getMacStatusResponse.systems.forEach((system) => {
          const systemName = this.getFullSystemName(system.system_name);
          const lastErrorResponse = this.convertErrorResponse(system.last_error_response);
          const ecuStatus = macKey.convertInspectionEcuStatus(system, getMacStatusResponse.error_code, getMacStatusResponse.platform_gen);
          const formattedEcuStatus = this.$t(ecuStatus.messageKey) + ecuStatus.endDigit;
          const dtcDetectionStatus = system.detection ? this.$t('mac.ecu_status_detected') : this.$t('mac.ecu_status_not_detected');

          convertedSystems.push({
            system_name: systemName,
            last_error_response: lastErrorResponse,
            ecu_status: formattedEcuStatus,
            dtc_detection_status: dtcDetectionStatus
          });
        });
      }

      this.macStatusInfo = {
        systems: convertedSystems,
        error_code: getMacStatusResponse.error_code,
        error_message_key: progress.errMsgKey,
        platform_gen: getMacStatusResponse.platform_gen,
        comm_target_ecu: convertedSystems.length > 0 ? convertedSystems[0].system_name : ''
      };
    },

    /**
     * IG-ON指示画面でボタンを押下した際の処理
     * @param {String} action ボタンの種類
     * @param {String} nextDialogId 次に表示されるダイアログの要素のid
     */
    turnOnIgnition: async function (action, nextDialogId) {
      try {
        this.modalShowInfo = { id: this.currentDialogId, isShow: false };
        if (action === 'ok') {
          this.dialogAction(nextDialogId);
        }
        const ignitionResponse = await api.postCall(config.MAC, '/sendIgnitionOn/' + this.netAppId, { action: action });
        const isSuccess = handler.validate(handler.validateTypes.all, ignitionResponse, this, null, () => { },
          //bad response callback
          (result) => {
            const cause = result.causeType;
            const behaviorType = result.behaviorType;
            // logout if session is invalid or network error
            if (cause === handler.causeTypes.networkError ||
              cause === handler.causeTypes.invalidSessionToken ||
              cause === handler.causeTypes.clientConnectorNotAliveError) {
              alert.showWarning(this, null, result.message, () => {
                if (behaviorType === handler.behaviorTypes.logout) {
                  this.logout(this);
                } else if (behaviorType === handler.behaviorTypes.resetSession) {
                  this.resetSession(this);
                }
              });
            } else {
              // Other errors
              this.showUnexpectedErrorModal(result.message);
            }
            return;
          }, null, false
        );
        if (!isSuccess) {
          this.modalShowInfo = { id: this.currentDialogId, isShow: false };
          return;
        }

        // Start Mac status monitoring
        if (action === 'ok') {
          this.setupWatcher();
        }
      } catch (error) {
        this.modalShowInfo = { id: this.currentDialogId, isShow: false };
      }
    },

    /**
     * ポーリング処理を行う
     */
    setupWatcher: function () {
      this.executeRspMsgCheckTimeout = Number(process.env.VUE_APP_MAC_CC_RSP_MAX_INTERVAL);
      this.watcher = this.$startInterval(() => {
        this.getMacStatus();
      }, process.env.VUE_APP_RETRY_INTERVAL, "GetMacStatus");
    },

    /**
     * ポーリング処理を停止する
     */
    clearWatcher: function () {
      if (this.watcher) {
        this.$stopInterval(this.watcher);
        this.watcher = null;
      }
      this.executeRspMsgCheckTimeout = Number(process.env.VUE_APP_MAC_CC_RSP_MAX_INTERVAL);
    },

    /**
     * クラウドコネクター利用不可エラーを表示する
     */
    showCCNotAliveModal: function () {
      this.clearWatcher();
      alert.showError(this, null, this.$t(api.getErrorMessageForCode(error.CLOUD_CONNECTOR_NOT_ACTIVE)),
        () => { this.logout(this); }
      );
    },

    /**
     * 例外エラーを表示する
     * @param {String} msg エラーメッセージ
     */
    showUnexpectedErrorModal: function (msg) {
      this.clearWatcher();
      alert.showError(this, null, msg, () => { this.logout(this); });
    },

    /**
     * MAC鍵更新処理を実行する
     */
    onUpdateButtonClick: async function () {
      try {
        // イベントログのページビューカウント
        eventLogUtil.eventLogPageViewCount(constants.EVENT_LOG_PAGE_VIEW_PAGE_NAME_MAC_KEY);

        // MAC鍵のカウントを1つ加算する
        this.usageCountUp({ type: config.USAGE_COUNT_MACKEY });
        this.currentDialogId = "";
        this.dialogAction('modal-platform-gen-decision');
        const token = session.getToken();

        await this.createEventLogRecord(constants.FUNCTION_NAME_REGISTRATION_MAC_KEY);
        const macUpdateResponse = await api.postCall(config.MAC, '/startMacKeyUpdate/' + this.netAppId, {
          id_token: token.id_token,
        });

        const isSuccess = handler.validate(handler.validateTypes.all, macUpdateResponse, this, null, () => { },
          (result) => {
            const cause = result.causeType;
            const behaviorType = result.behaviorType;
            // Logout if session is invalid or network error
            if (cause === handler.causeTypes.networkError ||
              cause === handler.causeTypes.invalidSessionToken) {
              this.clearWatcher();
              alert.showWarning(this, null, result.message, () => {
                if (behaviorType === handler.behaviorTypes.logout) {
                  this.logout(this);
                } else if (behaviorType === handler.behaviorTypes.resetSession) {
                  this.resetSession(this);
                }
              });
              return;
            } else {
              // Other errors
              this.showUnexpectedErrorModal(result.message);
            }
            return;
          }, null, false);
        if (!isSuccess) {
          this.modalShowInfo = { id: this.currentDialogId, isShow: false };
          return;
        }

        // Update Mac status monitoring
        this.setupWatcher();
      } catch (error) {
        this.modalShowInfo = { id: 'modal-key-inspection-progress', isShow: false };
      }
    },

    /**
     * ダイアログ表示を切り替える
     * @param {String} nextDialogId 次に表示するダイアログのID
     * @param {Boolean} stopWatching ポーリング停止の可否
     */
    dialogAction: function (nextDialogId, stopWatching = true) {
      if (stopWatching) this.clearWatcher();
      // 切り替え前後でダイアログが異なる場合のみ処理を行う
      if (this.currentDialogId !== nextDialogId) {
        if (this.currentDialogId) {
          this.modalShowInfo = { id: this.currentDialogId, isShow: false, nextDialog: nextDialogId };
        } else {
          this.modalShowInfo = { id: nextDialogId, isShow: true };
        }
        this.currentDialogId = nextDialogId;
      }
    },

    /**
     * MAC鍵更新のダイアログ切り替え処理を行う
     * @param {Object} getMacStatusResponse 現在のMAC鍵ステータス
     */
    macKeyUpdateProcess: function (getMacStatusResponse) {
      const progress = macKey.getMacKeyProgress(getMacStatusResponse, constants.MAC_KEY_PROCESS_UPDATE);
      this.dialogAction(progress.dialogId, progress.stopWatching);

      // ダイアログ表示に使用するプロパティを変換する
      const convertedSystems = [];
      if (getMacStatusResponse.systems && getMacStatusResponse.systems.length > 0) {
        getMacStatusResponse.systems.forEach((system) => {
          const systemName = this.getFullSystemName(system.system_name);
          const lastErrorResponse = this.convertErrorResponse(system.last_error_response);
          const ecuStatus = this.$t(macKey.convertUpdateEcuStatus(system, getMacStatusResponse.error_code, getMacStatusResponse.platform_gen));

          convertedSystems.push({
            system_name: systemName,
            last_error_response: lastErrorResponse,
            ecu_status: ecuStatus,
            mac_exec_status: system.mac_exec_status
          });
        });
      }

      this.macUpdateInfo = {
        systems: convertedSystems,
        error_code: getMacStatusResponse.error_code,
        error_message_key: progress.errMsgKey,
        mac_status: getMacStatusResponse.mac_status,
        platform_gen: getMacStatusResponse.platform_gen,
        comm_target_ecu: convertedSystems.length > 0 ? convertedSystems[0].system_name : ''
      };
    },

    /**
     * ECU名を表示用に変換する（該当するシステム名がi18にない場合は変換せずそのまま表示する）
     * @param {String} system_name MAC鍵ステータスに含まれているECU名
     * @returns 変換後のECU名
     */
    getFullSystemName: function (system_name) {
      let fullSysName = '';
      if ([constants.TLM_25ePF, MAC_SYSTEM_NAME.TLM_27BEV].includes(system_name)) {
        // 機器間認証用鍵登録の場合、「データコミュニケーションモジュール」と表示する
        fullSysName = `${this.$t('mac.auth_key_register_data_communication_module')}`;
      } else if ([MAC_SYSTEM_NAME.ADAS, MAC_SYSTEM_NAME.EV].includes(system_name)) {
        fullSysName = `${this.$t('mac.' + system_name)}`;
      } else if (MAC_KEY_SYSTEM_LIST.includes(system_name)) {
        fullSysName = `${this.$t('diagSytem.' + system_name)}`;
      } else {
        fullSysName = system_name;
      }
      return fullSysName;
    },

    /**
     * 診断通信エラー時にエラー種別を表示用に変換する
     * @param {String} lastErrorResponse MAC鍵ステータスに含まれているエラー種別
     * @returns 変換後のエラー種別
     */
    convertErrorResponse: function (lastErrorResponse) {
      let errorResponse = '';
      if (lastErrorResponse === 'no_response') {
        errorResponse = this.$t('mac.ecu_status_no_response');
      } else {
        errorResponse = `0x${lastErrorResponse}`;
      }
      return errorResponse;
    },

    /**
     * MAC鍵更新のIG-ON/OFF指示画面でボタンを押下した際の処理
     * @param {Number} exeType イベント/ボタンの種類
     */
    macUpdateEvent: async function (exeType) {
      if (exeType === 1) { // on ok button click, call sendIgOn
        this.sendIgOnForMacKeyUpdate('ok');
      } else if (exeType === 2) { // on quit button click, call sendIgOn
        this.sendIgOnForMacKeyUpdate('cancel');
      } else if (exeType === 3) { // on ok button click, call send Ig Off For Mac Key Update
        this.sendIgOffForMacKeyUpdate('ok');
      } else if (exeType === 4) { // on quit button click, call send Ig Off For Mac Key Update
        this.sendIgOffForMacKeyUpdate('cancel');
      }
    },

    /**
     * MAC鍵更新のIG-ON指示画面でボタンを押下した際の処理
     * @param {String} action ボタンの種類
    */
    sendIgOnForMacKeyUpdate: async function (action) {
      try {
        if (action === 'ok') {
          this.dialogAction('modal-mac-key-update-progress');
        } else {
          this.modalShowInfo = { id: this.currentDialogId, isShow: false };
        }
        const ignitionResponse = await api.postCall(config.MAC, '/sendIGOnUpdate/' + this.netAppId, { action: action });
        const isSuccess = handler.validate(handler.validateTypes.all, ignitionResponse, this, null, () => { },
          //bad response callback
          // eslint-disable-next-line max-lines
          (result) => {
            const cause = result.causeType;
            const behaviorType = result.behaviorType;
            // logout if session is invalid or network error
            if (cause === handler.causeTypes.networkError ||
              cause === handler.causeTypes.invalidSessionToken ||
              cause === handler.causeTypes.clientConnectorNotAliveError) {
              alert.showWarning(this, null, result.message, () => {
                if (behaviorType === handler.behaviorTypes.logout) {
                  this.logout(this);
                } else if (behaviorType === handler.behaviorTypes.resetSession) {
                  this.resetSession(this);
                }
              });
            } else {
              this.showUnexpectedErrorModal(result.message);
            }
            return;
          }, null, false
        );
        if (!isSuccess) {
          this.modalShowInfo = { id: this.currentDialogId, isShow: false };
          return;
        }

        // Start Mac status monitoring
        if (action === 'ok') {
          this.setupWatcher();
        }
      } catch (error) {
        this.modalShowInfo = { id: this.currentDialogId, isShow: false };
      }
    },

    /**
     * MAC鍵更新のIG-OFF指示画面でボタンを押下した際の処理
     * @param {String} action ボタンの種類
     */
    sendIgOffForMacKeyUpdate: async function (action) {
      try {
        if (action === 'ok') {
          this.dialogAction('modal-mac-key-update-progress');
        } else {
          this.modalShowInfo = { id: this.currentDialogId, isShow: false };
        }
        const sendIgOffResponse = await api.postCall(config.MAC, '/sendIGOffUpdate/' + this.netAppId, { action: action });
        const isSuccess = handler.validate(handler.validateTypes.all, sendIgOffResponse, this, null, () => { },
          //bad response callback
          (result) => {
            const cause = result.causeType;
            const behaviorType = result.behaviorType;
            // logout if session is invalid or network error
            if (cause === handler.causeTypes.networkError ||
              cause === handler.causeTypes.invalidSessionToken ||
              cause === handler.causeTypes.clientConnectorNotAliveError) {
              alert.showWarning(this, null, result.message, () => {
                if (behaviorType === handler.behaviorTypes.logout) {
                  this.logout(this);
                } else if (behaviorType === handler.behaviorTypes.resetSession) {
                  this.resetSession(this);
                }
              });
            } else {
              this.showUnexpectedErrorModal(result.message);
            }
            return;
          }, null, false
        );
        if (!isSuccess) {
          this.modalShowInfo = { id: this.currentDialogId, isShow: false };
          return;
        }

        // Start Mac status monitoring
        if (action === 'ok') {
          this.setupWatcher();
        }
      } catch (error) {
        // error occurred while send Ig off
      }
    },
    ////// End:  Mac Key update

    /**
     * 機器間認証用鍵登録処理を開始する
     */
    onRegistrationButtonClick: async function () {
      try {
        // イベントログのページビューカウント
        eventLogUtil.eventLogPageViewCount(constants.EVENT_LOG_PAGE_VIEW_PAGE_NAME_MAC_KEY);
        this.currentDialogId = "";
        this.dialogAction('modal-key-registration-progress');
        const token = session.getToken();

        await this.createEventLogRecord(constants.FUNCTION_NAME_REGISTRATION_AUTH_KEY);
        const startMacResponse = await api.postCall(config.MAC, '/startMacKeyRegistration/' + this.netAppId, {
          id_token: token.id_token,
        });

        const isSuccess = handler.validate(handler.validateTypes.all, startMacResponse, this, null, () => { },
          (result) => {
            const cause = result.causeType;
            const behaviorType = result.behaviorType;
            if (cause === handler.causeTypes.networkError ||
              cause === handler.causeTypes.invalidSessionToken) {
              this.clearWatcher();
              alert.showWarning(this, null, result.message, () => {
                if (behaviorType === handler.behaviorTypes.logout) {
                  this.logout(this);
                } else if (behaviorType === handler.behaviorTypes.resetSession) {
                  this.resetSession(this);
                }
              });
              return;
            } else {
              this.showUnexpectedErrorModal(result.message);
            }
            return;
          }, null, false);
        if (!isSuccess) {
          this.modalShowInfo = { id: 'modal-key-registration-progress', isShow: false };
          return;
        }
        this.setupWatcher(true);
      } catch (error) {
        this.modalShowInfo = { id: 'modal-key-registration-progress', isShow: false };
      }
    },


    /**
     * 機器間認証用鍵登録のダイアログ切り替え処理を行う
     * @param {Object} getMacStatusResponse 現在のステータス
     */
    authKeyRegistrationProcess: function (getMacStatusResponse) {
      const progress = macKey.getMacKeyProgress(getMacStatusResponse, constants.MAC_KEY_PROCESS_REGISTRATION);
      this.dialogAction(progress.dialogId, progress.stopWatching);

      // ダイアログ表示に使用するプロパティを変換する
      const convertedSystems = [];
      if (getMacStatusResponse.systems && getMacStatusResponse.systems.length > 0) {
        getMacStatusResponse.systems.forEach((system) => {
          const systemName = this.getFullSystemName(system.system_name);
          const lastErrorResponse = this.convertErrorResponse(system.last_error_response);
          const ecuStatus = macKey.convertRegistrationEcuStatus(system, getMacStatusResponse.error_code, getMacStatusResponse.platform_gen);
          const formattedEcuStatus = this.$t(ecuStatus.messageKey) + ecuStatus.endDigit;

          convertedSystems.push({
            system_name: systemName,
            last_error_response: lastErrorResponse,
            ecu_status: formattedEcuStatus,
          });
        });
      }

      this.authKeyStatusInfo = {
        systems: convertedSystems,
        error_code: getMacStatusResponse.error_code,
        error_message_key: progress.errMsgKey,
        mac_status: getMacStatusResponse.mac_status,
        comm_target_ecu: convertedSystems.length > 0 ? convertedSystems[0].system_name : ''
      };
    },


    /**
     * 処理完了フラグを設定する
     * @param {Boolean} value 処理完了フラグ
     */
    onSetEndOfProcessingFlag: function (value = true) {
      if (this.endOfProcessingFlag !== value) this.endOfProcessingFlag = value;
    },

    /**
     * home画面へ遷移する
     */
    onReturnToHomeOkButtonClick: async function () {
      this.modal_return_to_home_screen = false;
      this.$router.push({ name: 'home', params: { isAuto: false, isConfirmSkip: true } });
    },

    /**
     * イベントログを生成する
     * @param {String} functionName 機能名
     */
    createEventLogRecord: async function (functionName) {
      await api.postCall(
        config.MAC,
        '/createEventLogRecord/' + this.netAppId,
        { function_name: functionName }
      );
    },
  },

  created() {
    const pageObject = this.response ? JSON.parse(this.response) : null;
    if (pageObject !== null && pageObject !== undefined && macKey.isRunningMacKey(pageObject.mac_status)) {
      this.isReturnedFromDisconnection = true;
      this.setupWatcher();
    }
  },

  mounted() {
    this.$emit('setVehicleTabsAvailable', true);
  },

  beforeUnmount() {
    this.clearWatcher();
  },

};